import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, FolderPlus, Sparkles, X } from "lucide-react";
import { api, type CreateClusterInput, type Priority, type PlannerClusterNode, type PlannerPageType, type ClusterFamily, type ClusterIntentLabel } from "../../lib/api.js";

const FAMILIES: ClusterFamily[] = ["MTH", "LOC", "COND", "REM", "NUT", "WELL", "LONG", "PRO", "BRAND"];
const PAGE_TYPES: PlannerPageType[] = ["hub", "landing", "editorial", "comparison", "faq_section", "city_method", "pro_article"];
const INTENTS: ClusterIntentLabel[] = ["T", "C", "I", "B"];

/**
 * Create a new topical cluster from the UI — the missing "from scratch" path.
 *
 * Two entry points feed this:
 *   - "New cluster" button on the Pipeline header (build from scratch).
 *   - "+ Create new cluster…" in the Add-keyword dialog's cluster picker
 *     (pass `seedKeyword` to prefill the pillar/main keyword).
 *
 * The cluster id is auto-derived from the English name (editable). Once created,
 * the cluster's pillar keyword seeds DataForSEO when you later hit "Expand".
 */
export function NewClusterDialog({
  seedKeyword,
  allClusters,
  onCancel,
  onCreated,
}: {
  /** Optional main keyword to seed the cluster's pillar (from the add-keyword flow). */
  seedKeyword?: string;
  /** When provided, enables taxonomy placement (family / parent) — the planner passes the tree. */
  allClusters?: PlannerClusterNode[];
  onCancel: () => void;
  onCreated: (clusterId: string) => void;
}) {
  const [nameEn, setNameEn] = useState("");
  const [nameHe, setNameHe] = useState(seedKeyword ?? "");
  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [pillarHe, setPillarHe] = useState(seedKeyword ?? "");
  const [pillarEn, setPillarEn] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [target, setTarget] = useState<string>("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Taxonomy placement (optional — only surfaced when the tree is provided).
  const [family, setFamily] = useState<ClusterFamily | "">("");
  const [pageType, setPageType] = useState<PlannerPageType | "">("");
  const [intentLabel, setIntentLabel] = useState<ClusterIntentLabel | "">("");
  const [parentClusterId, setParentClusterId] = useState<string>("");

  // Auto-derive a slug id from the English name until the operator edits it.
  const derivedId = nameEn.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const effectiveId = idTouched ? id : derivedId;

  const create = useMutation({
    mutationFn: () => {
      const input: CreateClusterInput = {
        id: effectiveId || undefined,
        nameHe: nameHe.trim(),
        nameEn: nameEn.trim(),
        pillarKeywordHe: pillarHe.trim() || null,
        pillarKeywordEn: pillarEn.trim() || null,
        businessPriority: priority,
        targetArticleCount: target.trim() ? Number(target) : null,
        description: description.trim() || null,
        family: family || null,
        pageType: pageType || null,
        intentLabel: intentLabel || null,
        parentClusterId: parentClusterId || null,
      };
      return api.createCluster(input);
    },
    onSuccess: (r) => {
      if (!r.ok) { setError(r.error ?? "create failed"); return; }
      onCreated(r.cluster.id);
    },
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!nameEn.trim() || !nameHe.trim()) {
      setError("Both English and Hebrew cluster names are required.");
      return;
    }
    if (effectiveId && !/^[a-z0-9-]+$/.test(effectiveId)) {
      setError(`Cluster id "${effectiveId}" is invalid — lowercase letters, digits, hyphens only.`);
      return;
    }
    create.mutate();
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-sm animate-fade-in" onClick={onCancel} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <form
          onSubmit={handleSubmit}
          className="pointer-events-auto w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-rail animate-fade-in"
        >
          <header>
            <div className="flex items-center gap-2 text-2xs uppercase tracking-wider text-brand-700">
              <FolderPlus className="h-3.5 w-3.5" />
              New cluster
            </div>
            <h3 className="mt-1 text-lg font-semibold text-ink">Create a topical cluster</h3>
            <p className="mt-1 text-xs text-muted text-balance">
              Once created, set a pillar keyword and hit <strong>Expand</strong> on the cluster to auto-discover related keywords via DataForSEO + the classifier.
            </p>
          </header>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Name (English) <span className="text-danger">*</span></label>
                <input
                  type="text"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  placeholder="e.g. Homeopathy"
                  className="input"
                  autoFocus
                />
              </div>
              <div>
                <label className="label">Name (Hebrew) <span className="text-danger">*</span></label>
                <input
                  type="text"
                  value={nameHe}
                  onChange={(e) => setNameHe(e.target.value)}
                  placeholder="לדוגמה: הומאופתיה"
                  className="input"
                  dir="rtl"
                />
              </div>
            </div>

            <div>
              <label className="label">Cluster id (slug)</label>
              <input
                type="text"
                value={effectiveId}
                onChange={(e) => { setIdTouched(true); setId(e.target.value); }}
                placeholder="auto-derived from English name"
                className="input font-mono text-xs"
              />
              <p className="mt-1 text-2xs text-muted">Lowercase letters, digits, hyphens. Used as the stable id across keywords + links.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Main / pillar keyword (HE)</label>
                <input
                  type="text"
                  value={pillarHe}
                  onChange={(e) => setPillarHe(e.target.value)}
                  placeholder="seeds expansion, e.g. הומאופתיה"
                  className="input"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="label">Pillar keyword (EN)</label>
                <input
                  type="text"
                  value={pillarEn}
                  onChange={(e) => setPillarEn(e.target.value)}
                  placeholder="optional"
                  className="input"
                />
              </div>
              <div>
                <label className="label">Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value as Priority)} className="input">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="label">Target articles</label>
                <input
                  type="number"
                  min={0}
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="optional"
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="label">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="What this cluster covers, who it's for"
                className="textarea"
              />
            </div>

            {allClusters && (
              <div className="rounded-lg border border-line p-3">
                <div className="mb-2 text-2xs uppercase tracking-wider text-muted">Taxonomy placement</div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="label">Family</label>
                    <select value={family} onChange={(e) => setFamily(e.target.value as ClusterFamily | "")} className="input">
                      <option value="">—</option>
                      {FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Page type</label>
                    <select value={pageType} onChange={(e) => setPageType(e.target.value as PlannerPageType | "")} className="input">
                      <option value="">—</option>
                      {PAGE_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Intent</label>
                    <select value={intentLabel} onChange={(e) => setIntentLabel(e.target.value as ClusterIntentLabel | "")} className="input">
                      <option value="">—</option>
                      {INTENTS.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-2">
                  <label className="label">Parent cluster (the page this sits under — for a section like "price", pick the editorial page)</label>
                  <select value={parentClusterId} onChange={(e) => setParentClusterId(e.target.value)} className="input">
                    <option value="">— none (top-level) —</option>
                    {[...allClusters].sort((a, b) => `${a.family}${a.nameHe}`.localeCompare(`${b.family}${b.nameHe}`)).map((c) => (
                      <option key={c.id} value={c.id}>{c.family}{c.subFamily ? `/${c.subFamily}` : ""} — {c.nameHe} ({c.id})</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <footer className="flex items-center justify-end gap-2">
            <button type="button" onClick={onCancel} className="btn-secondary btn-sm" disabled={create.isPending}>
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-sm" disabled={create.isPending}>
              <Sparkles className="h-3.5 w-3.5" />
              {create.isPending ? "Creating…" : "Create cluster"}
            </button>
          </footer>
        </form>
      </div>
    </>
  );
}
